import {
  ELoaderSize,
  ETextStyleVariant,
  GeneralSearchIcon,
  Loader,
  Text,
} from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import React from 'react';
import { useFocusRing } from 'react-aria';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { ESearchFilters } from '@/constants/searchFilters';
import { vehicleTypes } from '@/constants/vehicleDetails';
import { IHeaderSearchFilterGuests } from '@/redux/modules/globalHeader';
import { getSearchFormRVProfileFilter } from '@/redux/selectors/search/searchForm';
import { TVehicleType } from '@/services/types/search/rentals/id';

// Bonfire's focus ring — the global `.focusRing` rule in bonfire.css — wired up the
// way Bonfire's own RawButton does it: react-aria sets the class only while the
// focus is visible, so the ring shows on Tab and stays off pointer clicks.
// `rounded-full` keeps the glow on the search bar's pill shape.
const useSearchFieldFocusRing = () => {
  const { isFocusVisible, focusProps } = useFocusRing();

  return { focusProps, focusRingClass: clsx('rounded-full', isFocusVisible && 'focusRing') };
};

interface IDateTab {
  onClick: () => void;
  selectedDates: string | null;
  variant?: 'minimal' | 'enhanced';
  redesigned?: boolean;
  // Open state of the panel this tab controls, and that panel's id — the button
  // has to announce whether it is expanded and what it expands.
  isOpen?: boolean;
  panelId?: string;
}

export const DateTab: React.FC<IDateTab> = ({
  onClick,
  selectedDates,
  variant,
  redesigned,
  isOpen = false,
  panelId,
}) => {
  const intl = useIntl();
  const { focusProps, focusRingClass } = useSearchFieldFocusRing();
  const label = intl.formatMessage({
    defaultMessage: 'Dates',
    id: 'TUk12c',
    description: 'Home Search Widget > Dates Filter Label',
  });

  const buttonLabel = intl.formatMessage(
    {
      defaultMessage: 'Click to {hasValue, select, true {change the} other {add}} dates',
      id: 'FiIHmX',
      description: 'Global Header > Search',
    },
    { hasValue: !!selectedDates },
  );

  // An aria-label replaces everything inside the button, so the selected range has
  // to be part of the name — otherwise the only way to hear the current dates is to
  // open the panel, and the visible text stops matching the name (WCAG 2.5.3). All
  // three tabs use this same shape.
  const selectedValueLabel = intl.formatMessage(
    {
      defaultMessage: '{label}: {value}',
      id: 'lXkGWL',
      description: 'Global Header > Search > Tab accessible name with its current value',
    },
    { label, value: selectedDates },
  );

  const panelProps = {
    ...focusProps,
    'aria-label': selectedDates ? selectedValueLabel : buttonLabel,
    'aria-expanded': isOpen,
    'aria-controls': isOpen ? panelId : undefined,
  };

  if (variant === 'enhanced') {
    return (
      <button
        type="button"
        onClick={onClick}
        className={clsx(
          'flex flex-col px-4 text-base text-left cursor-pointer justify-left border-neutral-20 text-secondary-base semiHighlight !border-l-0 md:w-48 md:!pl-6 gap-1',
          focusRingClass,
        )}
        {...panelProps}>
        <Text
          variant={ETextStyleVariant.LegalRegular}
          component="span"
          className="w-full text-gray-500 truncate">
          {label}
        </Text>

        <Text
          variant={ETextStyleVariant.LargeRegular}
          component="span"
          className="w-full truncate text-primary-base">
          {selectedDates ? (
            selectedDates
          ) : (
            <FormattedMessage defaultMessage="Add dates" id="Fzp8xF" />
          )}
        </Text>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'flex items-center justify-center h-full px-4 text-base cursor-pointer text-secondary-base semiHighlight md:min-w-[115px]',
        focusRingClass,
        redesigned
          ? "relative before:content-[''] before:absolute before:left-0 before:top-[20%] before:h-[60%] before:w-px before:bg-neutral-20"
          : 'border-l border-neutral-20',
        {
          ['!border-l-0 md:w-48 md:!justify-start md:!pl-6']: variant === 'minimal',
        },
      )}
      {...panelProps}>
      {selectedDates ? (
        <span className="font-medium truncate text-primary-base">{selectedDates}</span>
      ) : (
        <span className={clsx('font-medium', redesigned ? 'text-secondary-base' : 'text-gray-800')}>
          <FormattedMessage
            id="WY3YLi"
            defaultMessage="Any week"
            description="Home Search Widget > Dates Filter Placeholder (MP-5602 dateless mode)"
          />
        </span>
      )}
    </button>
  );
};

interface IGuestTabProps {
  onClick: () => void;
  guests?: IHeaderSearchFilterGuests;
  variant?: 'minimal' | 'enhanced';
  isGuestOccupancyAvailable?: boolean;
  redesigned?: boolean;
  isOpen?: boolean;
  panelId?: string;
}

export const GuestsTab: React.FC<IGuestTabProps> = ({
  onClick,
  guests,
  variant,
  isGuestOccupancyAvailable,
  redesigned,
  isOpen = false,
  panelId,
}) => {
  const intl = useIntl();
  const { focusProps, focusRingClass } = useSearchFieldFocusRing();

  const totalGuestValue =
    (guests?.adults || 0) +
    (guests?.children || 0) +
    (isGuestOccupancyAvailable ? guests?.infants || 0 : 0);
  const totalGuests = totalGuestValue || 0;

  const buttonLabel = intl.formatMessage(
    {
      defaultMessage: 'Click to {hasValue, select, true {change the} other {add}} guests',
      id: 'PB9EPa',
      description: 'Global Header > Search',
    },
    {
      hasValue: !!totalGuests,
    },
  );
  const placeholder =
    variant === 'enhanced'
      ? intl.formatMessage({
          defaultMessage: 'Add guests',
          id: 'atoxqO',
          description: 'Global Header > Placeholder text for header details field',
        })
      : intl.formatMessage({
          defaultMessage: 'Guests',
          id: 'ogcDqT',
          description: 'Global Header > Placeholder text for header details field',
        });
  const detailsValueText = intl.formatMessage(
    {
      defaultMessage: '{totalGuests, plural, one {# guest} other {# guests}}',
      id: 'YstT2S',
      description: 'Placeholder for header guests field',
    },
    {
      totalGuests,
    },
  );

  const label = intl.formatMessage({
    defaultMessage: 'Guests',
    id: 'p7nvv8',
    description: 'Home Search Widget > Guests Filter Label',
  });

  // Same shape as DateTab: the guest count is the button's visible text, so it has
  // to survive in the accessible name rather than being replaced by it.
  const selectedValueLabel = intl.formatMessage(
    {
      defaultMessage: '{label}: {value}',
      id: 'lXkGWL',
      description: 'Global Header > Search > Tab accessible name with its current value',
    },
    { label, value: detailsValueText },
  );

  const panelProps = {
    ...focusProps,
    'aria-label': totalGuests ? selectedValueLabel : buttonLabel,
    'aria-expanded': isOpen,
    'aria-controls': isOpen ? panelId : undefined,
  };

  if (variant === 'enhanced') {
    return (
      <button
        type="button"
        onClick={onClick}
        className={clsx(
          'flex flex-col px-4 text-base text-left cursor-pointer justify-left border-neutral-20 text-secondary-base semiHighlight !border-l-0 md:w-48 md:!pl-6 gap-1',
          focusRingClass,
        )}
        {...panelProps}>
        <Text
          variant={ETextStyleVariant.LegalRegular}
          component="span"
          className="w-full text-gray-500 truncate">
          {label}
        </Text>
        <Text
          variant={ETextStyleVariant.LargeRegular}
          component="span"
          className="w-full text-primary-base">
          {!totalGuests ? placeholder : detailsValueText}
        </Text>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'flex items-center justify-center h-full px-4 text-base cursor-pointer text-secondary-base semiHighlight md:min-w-[110px]',
        focusRingClass,
        redesigned
          ? "relative before:content-[''] before:absolute before:left-0 before:top-[20%] before:h-[60%] before:w-px before:bg-neutral-20"
          : 'border-l border-neutral-20',
        { ['md:w-48 md:!justify-start']: variant === 'minimal' },
      )}
      {...panelProps}>
      {!totalGuests ? (
        <span className={clsx('font-medium', redesigned ? 'text-secondary-base' : 'text-gray-800')}>
          {placeholder}
        </span>
      ) : (
        <span className="font-medium text-primary-base">{detailsValueText}</span>
      )}
    </button>
  );
};

interface ISearchButtonTabProps {
  loading: boolean;
  className?: string;
  variant?: 'minimal' | 'enhanced';
}

export const SearchButton: React.FC<ISearchButtonTabProps> = ({ loading, variant }) => {
  const intl = useIntl();
  // The same ring as the fields and tabs it sits next to. Both variants are already
  // `rounded-full`, which is the shape the ring takes.
  const { focusProps, focusRingClass } = useSearchFieldFocusRing();

  // Icon-only button: without this it reaches screen readers as an unnamed button.
  const buttonLabel = loading
    ? intl.formatMessage({
        defaultMessage: 'Searching',
        id: 'oIK3QL',
        description: 'Global Header > Search > Submit button while the search runs',
      })
    : intl.formatMessage({
        defaultMessage: 'Search',
        id: 'mgbPFH',
        description: 'Global Header > Search > Submit button',
      });

  if (variant === 'enhanced') {
    return (
      <div className="pl-2 pr-1.5 gap-4 rounded-r-[inherit]">
        <button
          className={clsx(
            'flex items-center justify-center h-full bg-gray-800 rounded-full p-7 shrink-0',
            focusRingClass,
          )}
          type="submit"
          data-testid="search-submit-btn"
          disabled={loading}
          aria-label={buttonLabel}
          {...focusProps}>
          {loading ? (
            <Loader size={ELoaderSize.Medium} onDark className="!w-[32px] !h-[32px]" />
          ) : (
            <GeneralSearchIcon width={32} height={32} color="white" aria-hidden />
          )}
        </button>
      </div>
    );
  }

  return (
    <button
      className={clsx(
        'flex items-center justify-center w-10 h-10 mr-1 bg-gray-800 rounded-full shrink-0',
        focusRingClass,
      )}
      type="submit"
      data-testid="search-submit-btn"
      disabled={loading}
      aria-label={buttonLabel}
      {...focusProps}>
      {loading ? (
        <Loader size={ELoaderSize.Small} onDark className="!w-[21px] !h-[21px]" />
      ) : (
        <GeneralSearchIcon width={21} height={21} color="white" aria-hidden />
      )}
    </button>
  );
};

interface IRVProfileTabProps {
  onClick: () => void;
  variant?: 'minimal' | 'enhanced';
  hasValue?: boolean;
  redesigned?: boolean;
  isOpen?: boolean;
  panelId?: string;
}

export const RVProfileTab: React.FC<IRVProfileTabProps> = ({
  onClick,
  variant,
  hasValue,
  redesigned,
  isOpen = false,
  panelId,
}) => {
  const intl = useIntl();
  const { focusProps, focusRingClass } = useSearchFieldFocusRing();

  const {
    [ESearchFilters.CAMPSITE_SUPPORTED_SUBTYPES]: rvProfileSubTypesFromStore,
    [ESearchFilters.CAMPSITE_SUPPORTED_LENGTH]: rvProfileLengthFromStore,
  } = useSelector(getSearchFormRVProfileFilter);

  const label = intl.formatMessage({
    defaultMessage: 'RV profile',
    id: 'Equq1B',
    description: 'RV Profile Filter Label',
  });
  const buttonLabel = intl.formatMessage(
    {
      defaultMessage: 'Click to {hasValue, select, true {change the} other {add}} RV profile',
      id: 'YCs/Dx',
      description: 'Global Header > Search',
    },
    { hasValue: !!hasValue },
  );

  const lengthLabel = rvProfileLengthFromStore
    ? intl.formatMessage(
        {
          defaultMessage: '{rvProfileLengthFromStore} ft',
          id: 'UC+UMO',
          description: 'RV Profile Filter Length Label',
        },
        {
          rvProfileLengthFromStore,
        },
      )
    : null;
  const vehicleType =
    rvProfileSubTypesFromStore &&
    rvProfileSubTypesFromStore.length > 0 &&
    (rvProfileSubTypesFromStore[0] as TVehicleType);
  const vehicleTypeLabel = vehicleType && intl.formatMessage(vehicleTypes[vehicleType].label);
  let detailsValueText = '';
  if (lengthLabel) {
    detailsValueText += lengthLabel;
  }
  if (vehicleTypeLabel) {
    if (lengthLabel) {
      detailsValueText += ' ';
    }
    detailsValueText += vehicleTypeLabel;
  }

  // Same shape as DateTab: keep the profile the user set inside the name instead of
  // replacing it. `hasValue` can be true with nothing to show (a slideouts-only
  // profile), so an empty value falls back to the label rather than "RV profile: ".
  const selectedValueLabel = intl.formatMessage(
    {
      defaultMessage: '{label}: {value}',
      id: 'lXkGWL',
      description: 'Global Header > Search > Tab accessible name with its current value',
    },
    { label, value: detailsValueText },
  );

  const panelProps = {
    ...focusProps,
    'aria-label': hasValue && detailsValueText ? selectedValueLabel : buttonLabel,
    'aria-expanded': isOpen,
    'aria-controls': isOpen ? panelId : undefined,
  };

  if (variant === 'enhanced') {
    return (
      <button
        type="button"
        onClick={onClick}
        className={clsx(
          'flex flex-col px-4 text-base text-left cursor-pointer justify-left border-neutral-20 text-secondary-base semiHighlight !border-l-0 md:w-48 md:!pl-6 gap-1',
          focusRingClass,
        )}
        {...panelProps}>
        <Text
          variant={ETextStyleVariant.LegalRegular}
          component="span"
          className="w-full text-gray-500 truncate">
          {label}
        </Text>

        <Text
          variant={ETextStyleVariant.LargeRegular}
          component="span"
          className="w-full text-primary-base">
          {hasValue ? (
            <FormattedMessage defaultMessage="Set" id="HnxG15" />
          ) : (
            <FormattedMessage defaultMessage="Add RV profile" id="BzhTZI" />
          )}
        </Text>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'flex items-center justify-center h-full px-4 text-base cursor-pointer text-secondary-base semiHighlight md:min-w-[115px]',
        focusRingClass,
        redesigned
          ? "relative before:content-[''] before:absolute before:left-0 before:top-[20%] before:h-[60%] before:w-px before:bg-neutral-20"
          : 'border-l border-neutral-20',
        {
          ['md:w-48 md:!justify-start']: variant === 'minimal',
        },
      )}
      {...panelProps}>
      {hasValue ? (
        <span className="font-medium text-primary-base">{detailsValueText}</span>
      ) : (
        <span className={clsx('font-medium', redesigned ? 'text-secondary-base' : 'text-gray-800')}>
          {label}
        </span>
      )}
    </button>
  );
};
